import React from "react";
import { Activity, Calendar, Loader2, ChevronDown, ChevronRight, Download, BarChart3 } from "lucide-react";
import { formatDateToLocaleString } from "@/shared/utils/date-utils";
import { useRegionUsage } from "@/domains/datapool-consumptions/services/datapool-usages";
import { getCurrentMonthDataUsage, getPreviousMonthDataUsage } from "@/shared/utils/serviceLineCalculations";
import { formatDataAmount, formatDataUsage } from "@/shared/utils/format-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { DatapoolConsumptionChartDialog } from "@/domains/datapool-consumptions/dialogs/DatapoolConsumptionChartDialog";
import ContainerLayout from '@/shared/components/ContainerLayout';
import { useUser } from "@/contexts/UserContext";
import EmptyView from "@/shared/components/EmptyView";
import { exportToCsv } from "@/shared/utils/csv-export-utils";
import { DataConsumptionType, getDataConsumptionTypeChartFill, getDataConsumptionTypeTextColorClass } from "@/shared/utils/color-utils-data-usages";
import { DatapoolDailyUsageSection } from "../components/DatapoolDailyUsageSection";

const DatapoolConsumptionPage = () => {
    const { selectedRegionCode } = useUser();
    const [openSections, setOpenSections] = React.useState<Set<number>>(new Set());
    const [isChartDialogOpen, setIsChartDialogOpen] = React.useState(false);

    // Fetch region usage data for the all months
    const {
        data: dataUsagesResponse,
        isLoading,
        error,
        refetch: refetchRegionUsage,
    } = useRegionUsage(selectedRegionCode, undefined);

    // Extract dataUsages from response
    const sortedDataUsages = React.useMemo(() => {
        if (!dataUsagesResponse || !dataUsagesResponse.results || dataUsagesResponse.results.length === 0) {
            return [];
        }
        return [...dataUsagesResponse.results].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    }, [dataUsagesResponse]);

    const toggleSection = (index: number) => {
        const newOpenSections = new Set(openSections);
        if (newOpenSections.has(index)) {
            newOpenSections.delete(index);
        } else {
            newOpenSections.add(index);
        }
        setOpenSections(newOpenSections);
    };

    // Calculate overall statistics
    const currentMonthUsage = React.useMemo(() => {
        return getCurrentMonthDataUsage(sortedDataUsages);
    }, [sortedDataUsages]);

    const lastMonthUsage = React.useMemo(() => {
        return getPreviousMonthDataUsage(sortedDataUsages);
    }, [sortedDataUsages]);

    // Calculate total usage across all periods
    const totalUsage = React.useMemo(() => {
        const total = dataUsagesResponse?.allTimeDataUsageGB || 0;
        return total;
    }, [dataUsagesResponse]);

    const handleViewChart = () => {
        setIsChartDialogOpen(true);
    };

    const handleCloseChartDialog = () => {
        setIsChartDialogOpen(false);
    };

    // Handle export functionality
    const handleExport = () => {
        if (!sortedDataUsages || sortedDataUsages.length === 0) {
            alert('No data to export');
            return;
        }

        const csvData = sortedDataUsages.map((usage) => {
            const monthName = formatDateToLocaleString(usage.startDate, { year: 'numeric', month: 'long' });

            return {
                'Month': monthName,
                'Consumption (GB)': formatDataAmount(usage.totalDataUsageGB || 0) || '0',
            };
        });

        exportToCsv(csvData, `datapool_consumption_${selectedRegionCode || 'region'}`);
    };

    return (
        <ContainerLayout shouldShowSidebar={true} shouldAllowRegionChange={true} shouldAllowAccountChange={false}>
            <div className="flex flex-col flex-1 p-4 space-y-4">
                {/* Fixed Header Section */}
                <div className="p-4 flex items-center justify-between rounded-lg bg-muted">
                    <div>
                        <h1 className="text-3xl font-bold">Datapool Consumption</h1>
                        <p className="text-lg">Overall datapool consumption overview for <span className="font-bold text-blue-500">{selectedRegionCode}</span> datapool</p>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p className="text-sm">Loading Datapool Consumption Data...</p>
                    </div>
                ) : (
                    error ? (
                        <EmptyView
                            icon={BarChart3}
                            title="Error Loading Datapool Consumption Data"
                            description="Error loading datapool consumption data at the moment. Please try again."
                            actionLabel="Retry"
                            onAction={() => refetchRegionUsage()}
                        />
                    ) : (
                        sortedDataUsages && sortedDataUsages.length > 0 ? (
                            <>
                                {/* Scrollable Content Section */}
                                <div className="flex-1 overflow-y-auto">
                                    <div className="space-y-4">
                                        {/* Main Usage Stats - Responsive Grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {/* Current Month's Consumption */}
                                            <Card className="relative overflow-hidden">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-md font-medium flex items-center gap-2">
                                                        <Activity className="h-4 w-4" />
                                                        Current Month's Consumption
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.CONSUMED)}>{formatDataUsage(currentMonthUsage?.totalDataUsageGB || 0)}</span></div>
                                                    <p className="text-xs">This month's consumption</p>
                                                </CardContent>
                                                <div className="absolute top-0 right-0 w-20 h-20 bg-green-50 rounded-full -translate-y-10 translate-x-10 opacity-30" />
                                            </Card>

                                            {/* Previous Month's Consumption */}
                                            <Card className="relative overflow-hidden">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-md font-medium flex items-center gap-2">
                                                        <Activity className="h-4 w-4" />
                                                        Previous Month's Consumption
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.PREVIOUS_MONTH)}>{formatDataUsage(lastMonthUsage?.totalDataUsageGB || 0)}</span></div>
                                                    <p className="text-xs">Previous month's total consumption</p>
                                                </CardContent>
                                                <div className="absolute top-0 right-0 w-20 h-20 bg-orange-50 rounded-full -translate-y-10 translate-x-10 opacity-30" />
                                            </Card>

                                            {/* Total Usage */}
                                            <Card className="relative overflow-hidden">
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-md font-medium flex items-center gap-2">
                                                        <Activity className="h-4 w-4" />
                                                        Total Consumption
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-3">
                                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.TOTAL)}>{formatDataUsage(totalUsage)}</span></div>
                                                    <p className="text-xs">All time total consumption</p>
                                                </CardContent>
                                                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-50 rounded-full -translate-y-10 translate-x-10 opacity-30" />
                                            </Card>
                                        </div>

                                        {/* Monthly Billing Cycles - Enhanced Layout */}
                                        <div className="space-y-4">
                                            <div className="glass-panel p-4 flex items-center justify-between rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-5 w-5" />
                                                    <h4 className="text-lg font-semibold">Monthly Breakdown</h4>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button onClick={handleViewChart} className="gap-2 bg-blue-500 hover:bg-blue-600">
                                                        <BarChart3 className="h-4 w-4" />
                                                        View Chart
                                                    </Button>
                                                    <Button onClick={handleExport} className="gap-2">
                                                        <Download className="h-4 w-4" />
                                                        Export Data
                                                    </Button>
                                                </div>
                                            </div>

                                            {sortedDataUsages.map((dataUsage, monthIndex) => (
                                                <Card key={monthIndex} className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow duration-200">
                                                    <CardHeader className="p-4">
                                                        <div className="flex flex-wrap items-center justify-between gap-4">
                                                            <div className="flex items-center gap-2">
                                                                <Calendar className="h-4 w-4 text-blue-500" />
                                                                <span className="text-lg font-semibold">
                                                                    {formatDateToLocaleString(dataUsage.startDate, { year: 'numeric', month: 'long' })}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-wrap items-stretch gap-2">
                                                                <div className="rounded-lg border bg-background p-2">
                                                                    <span className="text-xs">Priority Data</span>
                                                                    <div
                                                                        className="text-lg font-semibold"
                                                                        style={{ color: getDataConsumptionTypeChartFill(DataConsumptionType.PRIORITY_DATA) }}
                                                                    >
                                                                        {formatDataUsage(dataUsage.totalDataUsageGB)}
                                                                    </div>
                                                                </div>
                                                                <div className="rounded-lg border bg-background p-2">
                                                                    <span className="text-xs">Standard Data</span>
                                                                    <div
                                                                        className="text-lg font-semibold"
                                                                        style={{ color: getDataConsumptionTypeChartFill(DataConsumptionType.STANDARD_DATA) }}
                                                                    >
                                                                        {formatDataUsage(dataUsage.totalStandardGB)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </CardHeader>

                                                    {dataUsage.totalDataUsageGB > 0 && (
                                                        <CardContent className="px-4 pb-4">
                                                            <Collapsible
                                                                open={openSections.has(monthIndex)}
                                                                onOpenChange={() => toggleSection(monthIndex)}
                                                            >
                                                                <CollapsibleTrigger asChild>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="w-full justify-between h-10"
                                                                    >
                                                                        <span className="text-sm font-medium text-blue-500">Daily Usage Breakdown</span>
                                                                        {openSections.has(monthIndex) ? (
                                                                            <ChevronDown className="h-4 w-4 text-blue-500" />
                                                                        ) : (
                                                                            <ChevronRight className="h-4 w-4 text-blue-500" />
                                                                        )}
                                                                    </Button>
                                                                </CollapsibleTrigger>

                                                                <CollapsibleContent className="space-y-4 pt-4">
                                                                    <div className="w-full overflow-hidden rounded-lg border bg-background p-4">
                                                                        <DatapoolDailyUsageSection regionCode={selectedRegionCode} startDate={dataUsage.startDate} />
                                                                    </div>
                                                                </CollapsibleContent>
                                                            </Collapsible>
                                                        </CardContent>
                                                    )}
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <DatapoolConsumptionChartDialog
                                    organizationId={undefined}
                                    dataUsages={[...sortedDataUsages]}
                                    regionCode={selectedRegionCode}
                                    isOpen={isChartDialogOpen}
                                    onClose={handleCloseChartDialog}
                                />
                            </>
                        ) : (
                            <EmptyView
                                icon={BarChart3}
                                title="No Datapool Consumption Data Available"
                                description="No datapool consumption data available at the moment."
                            />
                        ))
                )}
            </div>
        </ContainerLayout>
    );
}

export default DatapoolConsumptionPage;
